/**
 * App Catalogue — Overview page.
 *
 * Landing page for the App Catalogue domain app (`/admin/app-catalogue`).
 * There is no JSON metrics endpoint (only Prometheus /metrics text), so every
 * stat here is computed CLIENT-SIDE from `getAllApps()`:
 *  - total apps, enabled vs disabled
 *  - counts by category and by audience
 *
 * Plus quick-link cards into the other pages of the domain app.
 */

import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconApps,
  IconCircleCheck,
  IconCircleOff,
  IconSparkles,
  IconShieldCheck,
  IconFlag,
  IconArrowRight,
  IconTag,
  IconUsers,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Title,
  Text,
  Badge,
  Paper,
  SimpleGrid,
  ThemeIcon,
  Button,
  Alert,
  Loader,
  Center,
} from '@/components/ui';
import { FadeIn, HoverLift } from '@/components/animations';
import { useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';

const CATALOGUE_QUERY_KEY = ['admin', 'app-catalogue', 'all'];

interface QuickLink {
  label: string;
  description: string;
  to: string;
  icon: ReactNode;
  color: string;
}

const QUICK_LINKS: QuickLink[] = [
  {
    label: 'Apps',
    description: 'Browse, create, edit, enable or delete app manifests.',
    to: ADMIN_ROUTES.APP_CATALOGUE_APPS,
    icon: <IconApps size={20} />,
    color: 'blue',
  },
  {
    label: 'Recommendations',
    description: 'Review ranked per-user app recommendations and recompute.',
    to: ADMIN_ROUTES.APP_CATALOGUE_RECOMMENDATIONS,
    icon: <IconSparkles size={20} />,
    color: 'violet',
  },
  {
    label: 'Entitlement Preview',
    description: 'Smoke-test which users a manifest entitlement would let in.',
    to: ADMIN_ROUTES.APP_CATALOGUE_ENTITLEMENTS,
    icon: <IconShieldCheck size={20} />,
    color: 'teal',
  },
  {
    label: 'Features & Permissions',
    description: 'Edit app-level entitlements and per-feature on/off flags.',
    to: ADMIN_ROUTES.APP_CATALOGUE_FEATURES,
    icon: <IconFlag size={20} />,
    color: 'orange',
  },
];

export default function OverviewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();

  const {
    data: apps = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const stats = useMemo(() => {
    const total = apps.length;
    const enabled = apps.filter((a) => a.enabled).length;
    const disabled = total - enabled;

    const byCategory = new Map<string, number>();
    const byAudience = new Map<string, number>();
    for (const app of apps as AppManifest[]) {
      for (const c of (app.categories as string[]) ?? []) {
        byCategory.set(c, (byCategory.get(c) ?? 0) + 1);
      }
      const aud = app.audience ?? 'staff';
      byAudience.set(aud, (byAudience.get(aud) ?? 0) + 1);
    }

    return {
      total,
      enabled,
      disabled,
      byCategory: Array.from(byCategory.entries()).sort((a, b) => b[1] - a[1]),
      byAudience: Array.from(byAudience.entries()).sort((a, b) => b[1] - a[1]),
    };
  }, [apps]);

  if (error) {
    return (
      <Stack gap="lg">
        <Title order={2}>App Catalogue</Title>
        <Alert color="red" title="Service Unavailable">
          <Text size="sm">
            {error instanceof Error ? error.message : 'Failed to load the app catalogue.'}
          </Text>
          <Button
            variant="light"
            color="red"
            size="xs"
            mt="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY })}
          >
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Title order={2}>App Catalogue</Title>
          <Text c="dimmed" size="sm">
            At-a-glance health of the CCMS mobile constellation — Hub and spoke apps.
          </Text>
        </div>

        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : (
          <>
            {/* Top-line stats */}
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <Paper withBorder p="md" radius="md">
                <Group>
                  <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                    <IconApps size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Total Apps
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.total}
                    </Text>
                  </div>
                </Group>
              </Paper>
              <Paper withBorder p="md" radius="md">
                <Group>
                  <ThemeIcon size="lg" radius="md" variant="light" color="green">
                    <IconCircleCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Enabled
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.enabled}
                    </Text>
                  </div>
                </Group>
              </Paper>
              <Paper withBorder p="md" radius="md">
                <Group>
                  <ThemeIcon size="lg" radius="md" variant="light" color="gray">
                    <IconCircleOff size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                      Disabled
                    </Text>
                    <Text size="xl" fw={700}>
                      {stats.disabled}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </SimpleGrid>

            {/* Breakdown cards */}
            <SimpleGrid cols={{ base: 1, md: 2 }}>
              <Card withBorder padding="lg" radius="md">
                <Group gap="xs" mb="md">
                  <IconTag size={18} />
                  <Title order={4}>By Category</Title>
                </Group>
                {stats.byCategory.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No categorised apps yet.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {stats.byCategory.map(([category, count]) => (
                      <Group key={category} justify="space-between">
                        <Badge variant="light" color="violet">
                          {category}
                        </Badge>
                        <Text fw={600}>{count}</Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Card>

              <Card withBorder padding="lg" radius="md">
                <Group gap="xs" mb="md">
                  <IconUsers size={18} />
                  <Title order={4}>By Audience</Title>
                </Group>
                {stats.byAudience.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    No apps yet.
                  </Text>
                ) : (
                  <Stack gap="xs">
                    {stats.byAudience.map(([audience, count]) => (
                      <Group key={audience} justify="space-between">
                        <Badge variant="light" color="orange">
                          {audience}
                        </Badge>
                        <Text fw={600}>{count}</Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Card>
            </SimpleGrid>

            {/* Quick links */}
            <div>
              <Title order={4} mb="sm">
                Manage
              </Title>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {QUICK_LINKS.map((link) => (
                  <HoverLift key={link.to} lift={4}>
                    <Card
                      withBorder
                      padding="lg"
                      radius="md"
                      h="100%"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(link.to)}
                    >
                      <Group justify="space-between" wrap="nowrap" align="flex-start">
                        <Group gap="sm" wrap="nowrap" align="flex-start">
                          <ThemeIcon size="lg" radius="md" variant="light" color={link.color}>
                            {link.icon}
                          </ThemeIcon>
                          <div>
                            <Text fw={600}>{link.label}</Text>
                            <Text size="sm" c="dimmed">
                              {link.description}
                            </Text>
                          </div>
                        </Group>
                        <IconArrowRight size={18} />
                      </Group>
                    </Card>
                  </HoverLift>
                ))}
              </SimpleGrid>
            </div>
          </>
        )}
      </Stack>
    </FadeIn>
  );
}
